import { useState } from 'react';
import Legal from './Legal';
import AboutPage from './AboutPage';
import { whatsappLink } from '../api';
import './LandingPageWhiteOrange.css';

const FEATURES = [
  { icon: '🧠', title: 'Persistent Memory',       desc: 'Remembers your goals, preferences, and context across every session — no repeating yourself.' },
  { icon: '📚', title: 'Literature Discovery',     desc: 'Search books, research papers, and news. Surface exactly what you need to know, instantly.' },
  { icon: '📧', title: 'Email & Calendar Agent',   desc: 'Ask in plain English — read emails, draft replies, and manage your calendar without opening your inbox.' },
  { icon: '🎓', title: 'AI Discuss',               desc: 'Talk through any topic with an AI that challenges, explains, and adapts to your level in 7 languages.' },
  { icon: '⚔️', title: 'Boardroom',             desc: 'Level up real workplace skills through AI-generated missions. Get graded. Earn XP. Grow faster.' },
  { icon: '🖥️', title: 'Presentation Builder',    desc: 'Turn a topic into a polished slide deck in seconds, backed by real research.' },
];

const STEPS = [
  { num: '1', title: 'Create your free account', desc: 'Sign up in seconds — no credit card required. Your personalised AI is ready the moment you log in.' },
  { num: '2', title: 'Connect your tools',        desc: 'Link Gmail, Google Calendar, and your documents. Everything in one place, without switching apps.' },
  { num: '3', title: 'Let GoodGPT do the work',   desc: "Chat, research, practise skills, or manage your inbox. GoodGPT handles the heavy lifting so you don't have to." },
];

const TESTIMONIALS = [
  {
    text: 'I was spending two hours every evening on research that should take thirty minutes. GoodGPT now handles the digging and gives me a clean summary. I actually close my laptop before 7 pm now.',
    name: 'Priya N.', role: 'Product Manager', initials: 'PN',
  },
  {
    text: "Three months into my PhD and I was drowning in papers. GoodGPT's literature feature found connections between studies I had completely missed. My supervisor noticed the difference immediately.",
    name: 'Daniel K.', role: 'Doctoral Researcher', initials: 'DK',
  },
  {
    text: "Running a small agency means context-switching all day. GoodGPT remembers every client's goals and tone, so I can jump between projects without losing the thread.",
    name: 'Sasha M.', role: 'Agency Owner', initials: 'SM',
  },
];

const GRAPH_FEATURES = [
  {
    icon: '🕸️',
    title: 'Memory as a Knowledge Graph',
    desc: 'Every goal, preference, and topic you mention is stored as a connected node — not a flat list. GoodGPT traces relationships between ideas so responses feel coherent weeks later.',
  },
  {
    icon: '📬',
    title: 'Email Contact Graph',
    desc: 'Tracks who you communicate with, how often, and about what. When you compose a reply, GoodGPT already knows the relationship history, tone, and open threads.',
  },
  {
    icon: '🗺️',
    title: 'Skill Dependency Map',
    desc: 'Boardroom maps skill prerequisites in a graph. After each mission, you see exactly which skills you can unlock next — and what\'s standing in the way.',
  },
  {
    icon: '🔗',
    title: 'Cross-Session Threading',
    desc: 'Topics from past sessions stay connected. Revisit a subject days later and GoodGPT surfaces the relevant prior context automatically — no searching, no re-explaining.',
  },
  {
    icon: '🎯',
    title: 'Personalisation Graph',
    desc: 'Your communication style, preferred tone, and recurring interests are stored as preference nodes. Every response is shaped by them — without you having to ask.',
  },
];

const PILLS = ['Smart Chat', 'Persistent Memory', 'Email Agent', 'Career RPG', 'AI Discuss', 'Literature Search', 'Presentations', 'Voice Notes'];

const FAQS = [
  {
    q: 'Is GoodGPT free to use?',
    a: 'Yes — you can sign up and start using GoodGPT at no cost. A free plan gives you access to core features including smart chat, memory, and literature search. Paid plans unlock higher usage limits and advanced capabilities.',
  },
  {
    q: 'How is GoodGPT different from ChatGPT?',
    a: "GoodGPT is built around persistent memory — it learns your goals, preferences, and communication style over time so you never have to repeat yourself. It also bundles specialised tools like an email and calendar agent, career skills training, and presentation building that go far beyond general-purpose chat.",
  },
  {
    q: 'Does GoodGPT really remember my conversations?',
    a: "Yes. GoodGPT uses a semantic memory system that stores important context from your conversations and recalls it intelligently when it's relevant — even weeks later. You stay in control and can view or clear your memory at any time from your profile.",
  },
  {
    q: 'Which languages does GoodGPT support?',
    a: 'GoodGPT currently supports 7 languages for AI Discuss and voice-based interactions, including English, Hindi, Spanish, French, German, Portuguese, and Japanese. More languages are on the way.',
  },
  {
    q: 'Can I connect my Gmail and Google Calendar?',
    a: "Yes. GoodGPT's Email & Calendar Agent lets you read emails, draft replies, schedule meetings, and manage your calendar through plain-English commands — without ever leaving the app.",
  },
  {
    q: 'How is my data kept private?',
    a: 'Your data is encrypted in transit and at rest. GoodGPT never sells or shares your personal information with third parties. You can delete your account and all associated data at any time from Settings.',
  },
];

export default function LandingPageWhiteOrange({ onGetStarted }) {
  const [legalTab, setLegalTab] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [showAbout, setShowAbout] = useState(false);

  return (
    <>
    <div className="lpwo">
      {/* Nav */}
      <nav className="lpwo-nav">
        <div className="lpwo-nav-inner">
          <div className="lpwo-logo">
            <span className="lpwo-logo-icon">🔆</span>
            <span className="lpwo-logo-text">Good<span>GPT</span></span>
          </div>
          <div className="lpwo-nav-actions">
            <button className="lpwo-btn-ghost-nav" onClick={onGetStarted}>Sign in</button>
            <button className="lpwo-btn-cta-sm" onClick={onGetStarted}>Get started free</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="lpwo-hero">
        <div className="lpwo-hero-inner">
          <div className="lpwo-hero-badge">🔥 Your AI. Your rules.</div>
          <h1 className="lpwo-hero-title">
            The AI assistant that's<br />
            <span>built around your life</span>
          </h1>
          <p className="lpwo-hero-sub">
            GoodGPT combines persistent memory, email management, career coaching,
            and deep research — all in one place, all remembering who you are.
          </p>
          <div className="lpwo-hero-cta">
            <button className="lpwo-btn-cta" onClick={onGetStarted}>
              Start for free →
            </button>
            <button className="lpwo-btn-outline" onClick={onGetStarted}>
              See how it works
            </button>
          </div>
          <a
            className="lpwo-hero-qr"
            href="https://play.google.com/store/apps/details?id=site.goodgpt.app&amp;pcampaignid=web_share"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get GoodGPT on Google Play"
          >
            <img className="lpwo-qr-img" src={`${process.env.PUBLIC_URL || ''}/playstore-qr.png`} alt="Scan to get GoodGPT on Google Play" width="180" height="180" />
            <div className="lpwo-qr-copy">
              <strong>Install on Android</strong>
              <span>Point your camera at the code to get the app.</span>
            </div>
          </a>
          <p className="lpwo-hero-note">No credit card required &nbsp;·&nbsp; Free plan available</p>

          <div className="lpwo-hero-video">
            <div className="lpwo-video-frame" style={{ position: 'relative', overflow: 'hidden', aspectRatio: '1920/1080' }}>
              <iframe
                src="https://share.synthesia.io/embeds/videos/b19628e0-bb1a-4528-a2a5-5ce764d9cbcc"
                loading="lazy"
                title="Synthesia video player - The workday bottleneck that slows every professional down"
                allowFullScreen
                allow="encrypted-media; fullscreen; microphone; screen-wake-lock;"
                style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, border: 'none', padding: 0, margin: 0, overflow: 'hidden' }}
              />
            </div>
          </div>

          <div className="lpwo-hero-pills">
            {PILLS.map((p) => <span key={p} className="lpwo-pill">{p}</span>)}
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="lpwo-stats">
        <div className="lpwo-stats-inner">
          <div className="lpwo-stat">
            <div className="lpwo-stat-val">8<span>+</span></div>
            <div className="lpwo-stat-label">Powerful tools in one app</div>
          </div>
          <div className="lpwo-stat">
            <div className="lpwo-stat-val">7<span>+</span></div>
            <div className="lpwo-stat-label">Languages supported</div>
          </div>
          <div className="lpwo-stat">
            <div className="lpwo-stat-val">0<span>₹</span></div>
            <div className="lpwo-stat-label">To get started today</div>
          </div>
          <div className="lpwo-stat">
            <div className="lpwo-stat-val">24<span>/7</span></div>
            <div className="lpwo-stat-label">Always available for you</div>
          </div>
        </div>
      </div>

      {/* Features */}
      <section className="lpwo-section">
        <div className="lpwo-section-inner">
          <div className="lpwo-section-eyebrow">Everything in one place</div>
          <h2 className="lpwo-section-title">One assistant. Endless capabilities.</h2>
          <div className="lpwo-feature-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="lpwo-feature-card">
                <span className="lpwo-feature-icon">{f.icon}</span>
                <div className="lpwo-feature-title">{f.title}</div>
                <div className="lpwo-feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Context Graph Intelligence */}
      <section className="lpwo-section lpwo-section-graph">
        <div className="lpwo-section-inner">
          <div className="lpwo-section-eyebrow">Under the hood</div>
          <h2 className="lpwo-section-title">The AI that truly knows you</h2>
          <p className="lpwo-section-sub">
            Most AI tools forget you the moment you close the tab. GoodGPT builds a living knowledge graph of your goals,
            relationships, and skills — connecting the dots across every session so you never have to repeat yourself.
          </p>
          <div className="lpwo-graph-grid">
            {GRAPH_FEATURES.map((f) => (
              <div key={f.title} className="lpwo-graph-card">
                <span className="lpwo-graph-icon">{f.icon}</span>
                <div className="lpwo-graph-title">{f.title}</div>
                <div className="lpwo-graph-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="lpwo-section lpwo-section-alt">
        <div className="lpwo-section-inner">
          <div className="lpwo-section-eyebrow">Get up and running in minutes</div>
          <h2 className="lpwo-section-title">Simple to start. Powerful over time.</h2>
          <div className="lpwo-steps">
            {STEPS.map((s) => (
              <div key={s.num} className="lpwo-step">
                <div className="lpwo-step-num">{s.num}</div>
                <div className="lpwo-step-title">{s.title}</div>
                <div className="lpwo-step-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="lpwo-section">
        <div className="lpwo-section-inner">
          <div className="lpwo-section-eyebrow">Real people. Real results.</div>
          <h2 className="lpwo-section-title">Loved by professionals everywhere</h2>
          <div className="lpwo-testimonial-grid">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="lpwo-testimonial-card">
                <div className="lpwo-stars">★★★★★</div>
                <p className="lpwo-testimonial-text">"{t.text}"</p>
                <div className="lpwo-testimonial-author">
                  <div className="lpwo-avatar">{t.initials}</div>
                  <div>
                    <div className="lpwo-author-name">{t.name}</div>
                    <div className="lpwo-author-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="lpwo-section lpwo-section-alt">
        <div className="lpwo-section-inner">
          <div className="lpwo-section-eyebrow">Got questions?</div>
          <h2 className="lpwo-section-title">Frequently asked questions</h2>
          <div className="lpwo-faq-list">
            {FAQS.map((faq, i) => (
              <div key={i} className={`lpwo-faq-item${openFaq === i ? ' lpwo-faq-open' : ''}`}>
                <button
                  className="lpwo-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{faq.q}</span>
                  <span className="lpwo-faq-icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <div className="lpwo-faq-a">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="lpwo-cta-banner">
        <h2 className="lpwo-cta-title">Ready to work smarter?</h2>
        <p className="lpwo-cta-sub">Join professionals using GoodGPT to stay sharp, organised, and ahead of the curve.</p>
        <button className="lpwo-btn-cta lpwo-btn-cta-white" onClick={onGetStarted}>
          Create your free account →
        </button>
      </section>

      {/* Footer */}
      <footer className="lpwo-footer">
        <div className="lpwo-footer-inner">
          <div className="lpwo-logo">
            <span className="lpwo-logo-icon">🔆</span>
            <span className="lpwo-logo-text lpwo-logo-text-dark">Good<span>GPT</span></span>
          </div>
          <div className="lpwo-footer-links">
            <a className="lpwo-social-link" href="https://www.linkedin.com/company/goodgptai" target="_blank" rel="noopener noreferrer" aria-label="GoodGPT on LinkedIn" title="GoodGPT on LinkedIn">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true" focusable="false">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <span className="lpwo-footer-sep">·</span>
            <button className="lpwo-legal-btn" onClick={() => setLegalTab('terms')}>Terms</button>
            <span className="lpwo-footer-sep">·</span>
            <button className="lpwo-legal-btn" onClick={() => setLegalTab('privacy')}>Privacy</button>
            <span className="lpwo-footer-sep">·</span>
            <button className="lpwo-legal-btn" onClick={() => setShowAbout(true)}>About Us</button>
          </div>
          <div className="lpwo-footer-copy">© {new Date().getFullYear()} GoodGPT. All rights reserved. GOSHOPIN NOW (OPC) PRIVATE LIMITED · GSTIN 06AALCG1828J1ZG · <a className="footer-contact-link" href="mailto:info@goodgpt.site">info@goodgpt.site</a>
            <br />C-042, 4th Floor, Supermart, DLF Phase IV, Galleria DLF-IV, Gurgaon, Haryana, 122009, India</div>
        </div>
      </footer>
    </div>

      {legalTab && <Legal initialTab={legalTab} onClose={() => setLegalTab(null)} />}
      {showAbout && <AboutPage onClose={() => setShowAbout(false)} />}

<a
          className="lpwo-whatsapp-btn"
          href={whatsappLink('Hi GoodGPT — I have a question.')}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with us on WhatsApp"
        >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span>Chat with us</span>
      </a>
    </>
  );
}
